import React, { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./lib/AuthContext";
import { publicApi } from "./lib/api";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import BackToTop from "./components/BackToTop";
import Home from "./pages/Home";
import About from "./pages/About";
import Explore from "./pages/Explore";
import Events from "./pages/Events";
import Contact from "./pages/Contact";
import ListingDetail from "./pages/ListingDetail";
import EventDetail from "./pages/EventDetail";
import Category from "./pages/Category";
import Subcategory from "./pages/Subcategory";
import JoinCCN from "./pages/JoinCCN";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import BusinessForm from "./pages/admin/BusinessForm";
import MembershipRequests from "./pages/admin/MembershipRequests";
import SiteSettings from "./pages/admin/SiteSettings";
import ImportExport from "./pages/admin/ImportExport";
import ImportImagesZip from "./pages/admin/ImportImagesZip";
import SystemInfo from "./pages/admin/SystemInfo";
import AdminChatbot from "./pages/admin/Chatbot";
import AdminEventsList from "./pages/admin/EventsList";
import AdminEventForm from "./pages/admin/EventForm";
import EventsDashboardV2 from "./pages/admin/events-v2/DashboardV2";
import EventEditorV2 from "./pages/admin/events-v2/EditorV2";
import MigrazioneJson from "./pages/admin/MigrazioneJson";
import { AnalyticsInjector } from "./components/AnalyticsInjector";
import SiteDashboard from "./pages/admin/site/SiteDashboard";
import PageEditor from "./pages/admin/site/PageEditor";
import UIStringsEditor from "./pages/admin/site/UIStringsEditor";
import SEOEditor from "./pages/admin/site/SEOEditor";
import RequireAdmin from "./pages/admin/RequireAdmin";
import ChatbotWidget from "./components/ChatbotWidget";

const BackendStatusBanner = () => {
  const [state, setState] = useState({ show: false });
  useEffect(() => {
    publicApi.health()
      .then((r) => setState({ show: !r.data.configured, status: r.data }))
      .catch(() => setState({ show: true, status: null }));
  }, []);
  if (!state.show) return null;
  return (
    <div className="bg-[#FFF9C4] border-b border-[#F4E500] text-[#1F2937] text-sm">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-2.5 flex items-start gap-2">
        <span className="w-2 h-2 rounded-full bg-[#F4E500] ring-2 ring-white mt-1.5" />
        <span>
          <strong>Setup in corso.</strong>{" "}
          Il database Supabase non è ancora configurato. Le pagine mostrano contenuti dimostrativi finché non vengono inserite le chiavi.
        </span>
      </div>
    </div>
  );
};

const PublicLayout = ({ children }) => (
  <>
    <Header />
    <BackendStatusBanner />
    <main>{children}</main>
    <Footer />
    <BackToTop />
    <ChatbotWidget />
  </>
);

const AdminLayout = ({ children }) => (
  <>
    {children}
    <BackToTop />
  </>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsInjector />
          <Routes>
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/chi-siamo" element={<PublicLayout><About /></PublicLayout>} />
            <Route path="/attivita" element={<PublicLayout><Explore /></PublicLayout>} />
            <Route path="/attivita/:id" element={<PublicLayout><ListingDetail /></PublicLayout>} />
            <Route path="/eventi" element={<PublicLayout><Events /></PublicLayout>} />
            <Route path="/eventi/:slug" element={<PublicLayout><EventDetail /></PublicLayout>} />
            <Route path="/categoria/:slug" element={<PublicLayout><Category /></PublicLayout>} />
            <Route path="/categoria/:catSlug/:subSlug" element={<PublicLayout><Subcategory /></PublicLayout>} />
            <Route path="/contatti" element={<PublicLayout><Contact /></PublicLayout>} />
            <Route path="/diventa-socio" element={<PublicLayout><JoinCCN /></PublicLayout>} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<RequireAdmin><AdminLayout><AdminDashboard /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/attivita/nuova" element={<RequireAdmin><AdminLayout><BusinessForm /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/attivita/:id" element={<RequireAdmin><AdminLayout><BusinessForm /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/richieste" element={<RequireAdmin><AdminLayout><MembershipRequests /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/impostazioni" element={<RequireAdmin><AdminLayout><SiteSettings /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/importazione" element={<RequireAdmin><AdminLayout><ImportExport /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/importazione/immagini" element={<RequireAdmin><AdminLayout><ImportImagesZip /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/informazioni" element={<RequireAdmin><AdminLayout><SystemInfo /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/chatbot" element={<RequireAdmin><AdminLayout><AdminChatbot /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/eventi" element={<RequireAdmin><AdminLayout><AdminEventsList /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/eventi/nuovo" element={<RequireAdmin><AdminLayout><AdminEventForm /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/eventi/:id" element={<RequireAdmin><AdminLayout><AdminEventForm /></AdminLayout></RequireAdmin>} />
            {/* Events V2 — Sprint 1. Guarded by feature flag events_v2_enabled (backend enforces) */}
            <Route path="/admin/eventi-v2" element={<RequireAdmin><AdminLayout><EventsDashboardV2 /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/eventi-v2/nuovo" element={<RequireAdmin><AdminLayout><EventEditorV2 /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/eventi-v2/:id" element={<RequireAdmin><AdminLayout><EventEditorV2 /></AdminLayout></RequireAdmin>} />

            {/* WordPress Migration (Sprint 3 · skeleton) */}
            <Route path="/admin/migrazione-json" element={<RequireAdmin><AdminLayout><MigrazioneJson /></AdminLayout></RequireAdmin>} />

            {/* CMS Editoriale (V11 Fase 2) */}
            <Route path="/admin/sito" element={<RequireAdmin><AdminLayout><SiteDashboard /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/sito/ui-strings" element={<RequireAdmin><AdminLayout><UIStringsEditor /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/sito/seo" element={<RequireAdmin><AdminLayout><SEOEditor /></AdminLayout></RequireAdmin>} />
            <Route path="/admin/sito/:pageKey" element={<RequireAdmin><AdminLayout><PageEditor /></AdminLayout></RequireAdmin>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
